// src/api/stock.ts
import { apiClient } from './client';
import type { ApiResponse } from '../types';
import type { WarehouseStock, ProductVariantStock, Warehouse } from '../types';

export interface StockAdjustment {
    product_id: number;
    variant_id?: number | null;
    warehouse_id: number;
    quantity_change: number; // positive for add, negative for remove
    reason: string;
    reason_text?: string;
    reference_number?: string;
    notes?: string;
}

export interface StockAdjustmentResponse {
    id: number;
    product_variant: number;
    warehouse: number;
    quantity_change: number;
    previous_quantity: number;
    new_quantity: number;
    reason: string;
    reason_text?: string;
    reference_number?: string;
    notes?: string;
    created_by: number;
    created_at: string;
}

export const stockAPI = {
    // Get stock levels for a specific product/variant
    async getProductStock(productId: number): Promise<ApiResponse<WarehouseStock[]>> {
        try {
            const response = await apiClient.get(`/products/${productId}/stock/`);
            return { data: response.data, status: response.status };
        } catch (error: any) {
            return {
                error: error.response?.data?.detail || 'Failed to fetch stock',
                status: error.response?.status || 500
            };
        }
    },

    // Get stock for a specific variant across all warehouses
    async getVariantStock(variantId: number): Promise<ApiResponse<ProductVariantStock[]>> {
        try {
            const response = await apiClient.get(`/variants/${variantId}/stock/`);
            return { data: response.data, status: response.status };
        } catch (error: any) {
            return {
                error: error.response?.data?.detail || 'Failed to fetch variant stock',
                status: error.response?.status || 500
            };
        }
    },

    // Get all warehouses
    async getWarehouses(): Promise<ApiResponse<Warehouse[]>> {
        try {
            const response = await apiClient.get('store/warehouses/');
            return { data: response.data, status: response.status };
        } catch (error: any) {
            return {
                error: error.response?.data?.detail || 'Failed to fetch warehouses',
                status: error.response?.status || 500
            };
        }
    },

    // Adjust stock (add/remove)
    async adjustStock(adjustment: StockAdjustment): Promise<ApiResponse<StockAdjustmentResponse>> {
        try {
            const response = await apiClient.post('/store/products/stock/adjust/', adjustment);
            return { data: response.data, status: response.status };
        } catch (error: any) {
            return {
                error: error.response?.data?.detail || 'Failed to adjust stock',
                status: error.response?.status || 500
            };
        }
    },

    // Get stock adjustment history for a product/variant
    async getAdjustmentHistory(productId: number, variantId?: number): Promise<ApiResponse<StockAdjustmentResponse[]>> {
        try {
            const params = variantId ? { variant_id: variantId } : {};
            const response = await apiClient.get(`/products/${productId}/stock/history/`, { params });
            return { data: response.data, status: response.status };
        } catch (error: any) {
            return {
                error: error.response?.data?.detail || 'Failed to fetch history',
                status: error.response?.status || 500
            };
        }
    }
};